import Image from 'components/Image';
import RoundedButton from 'components/RoundedButton';
import React from 'react';
import PaperPlaneIcon from 'reusable-components/icons/outline/PaperPlaneIcon';
import { useTrekker } from 'utils/analytics';
import { CONTACT_HOST_SOURCES } from '~/constants';
import useLocalizedNavigate from '~/hooks/useLocalizedNavigate';

const CSS = require('./HostSuccessSection.module.scss');

const IMAGE_OPTIONS = {
  width: 96,
  height: 120,
  gravity: 'faces',
};

interface HostSuccessSectionProps {
  name: string;
  image: string;
  personalTitle?: string;
  hostId: string;
  hostIsAvailable: boolean;
  source: CONTACT_HOST_SOURCES;
}

function HostSuccessSection({
  name,
  image,
  personalTitle,
  hostId,
  hostIsAvailable,
  source,
}: HostSuccessSectionProps) {
  const navigate = useLocalizedNavigate();
  const trekker = useTrekker();

  const goToChat = (event) => {
    trekker.event('click', {
      component: ['goToChatButton'],
      buttonText: event?.target?.textContent,
    });
    navigate(`/messages/${hostId}/`, { replace: true });
  };

  const goBack = (event) => {
    const urlParts = window.location.pathname.split('contact/');
    trekker.event('click', {
      component: ['takeMeBackButton'],
      buttonText: event?.target?.textContent,
    });
    navigate(urlParts[0], { replace: true });
  };

  return (
    <section className={CSS.HostSuccessSection}>
      <div className={CSS.LocalContainer}>
        <h3 className={CSS.LocalTitle}>
          {name}
          <span className={CSS.PersonalTitle}>{personalTitle}</span>
        </h3>
        <figure className={CSS.ImageContainer}>
          <Image
            src={image}
            options={IMAGE_OPTIONS}
            width={IMAGE_OPTIONS.width}
            height={IMAGE_OPTIONS.height}
            alt={`Withlocals host ${name}`}
            className={CSS.Image}
          />
          <div className={CSS.IconContainer}>
            <PaperPlaneIcon size={32} />
          </div>
        </figure>
      </div>
      <h2 className={CSS.Title}>Request sent to {name}</h2>
      {hostIsAvailable ? (
        <p className={CSS.Paragraph}>
          {name} has received your request and will get back to you soon to
          confirm the details. Get in touch to share any extra details or ask
          questions!
        </p>
      ) : source === CONTACT_HOST_SOURCES.HOST_PAGE ? (
        <p className={CSS.Paragraph}>
          {name} might not be available for the date and time you requested,
          would you like to forward your request to other hosts?
        </p>
      ) : (
        <p className={CSS.Paragraph}>
          {name} has received your request but may be unavailable for the
          requested date, time or duration. We recommend you to get in touch
          with other hosts to secure your experience.
        </p>
      )}
      <div className={CSS.ButtonContainer}>
        <RoundedButton
          onClick={goToChat}
          edgeRounding="medium"
          pink
          elevated
          size="S"
        >
          Say Hi to {name}
        </RoundedButton>
        <RoundedButton
          onClick={goBack}
          edgeRounding="medium"
          invertedBorder
          size="S"
        >
          Take me back
        </RoundedButton>
      </div>
    </section>
  );
}

export default HostSuccessSection;
